<template>
  <b-container>
    <section
      ref="booking-engine"
      v-click-outside="close_datepicker"
      :class="`booking-engine
      ${is_open ? 'booking-engine--is-open' : ''}
      ${customizations.glass ? 'booking-engine--glass' : ''}
      ${customizations.inline ? 'booking-engine--inline' : ''}
      ${has_super_in ? 'booking-engine--super-in' : ''}
      ${customizations.hide_location_search ? 'booking-engine--items-2' : 'booking-engine--items-3'}`"
    >
      <invadobay-super-in
        :super_in="super_in"
        @select-child="$emit('select-child', $event)"
      />
      <b-row>
        <!-- Location (autocomplete) -->
        <b-col v-if="!customizations.hide_location_search" md="4">
          <div class="vb-form">
            <button ref="hidden_input" class="focus-dump-button"></button>
            <!--serve per togliere il focus dagli autocomplete dopo il click, così poi al focus si seleziona di nuovo tutto il testo ed è più semplice cancellarlo-->
            <autocomplete
              ref="autocomplete"
              :key="location.id"
              base-class="booking-engine--autocomplete"
              :search="on_search_location"
              :placeholder="autocomplete_placeholder"
              :debounce-time="500"
              :submit-on-enter="true"
              :get-result-value="
                (r) => (r && r.type !== 'msg' && r.label) || ''
              "
              @update="on_autocomplete_update"
              @submit="on_submit_location"
            >
              <template
                #default="{
                  inputProps,
                  inputListeners,
                  results,
                  resultProps,
                  resultListProps,
                  resultListListeners
                }"
              >
                <div class="booking-engine--autocomplete--form">
                  <div class="booking-engine--autocomplete--icon">
                    <vb-icon
                      :name="location.match_level === 'point' ? 'map' : 'pin'"
                      color="--primary"
                      :size="20"
                    />
                  </div>
                  <input
                    v-if="autocomplete_ready"
                    ref="autocomplete-input"
                    type="search"
                    :class="[
                      'vb-input form-control',
                      { 'autocomplete-input-selected': autocomplete_selected },
                    ]"
                    v-bind="inputProps"
                    v-on="inputListeners"
                    @focus="
                      ($event) => {
                        autocomplete_focus = true;
                        scroll_into_view();
                        $event.target.select();
                      }
                    "
                    @blur="
                      () => {
                        autocomplete_focus = false;
                        autocomplete_blur();
                      }
                    "
                  />
                  <div
                    v-if="autocomplete_searching"
                    class="booking-engine--autocomplete--loading"
                  >
                    <loader />
                  </div>
                </div>

                <b-row v-if="results">
                  <b-col>
                    <ul
                      v-bind="resultListProps"
                      class="booking-engine--autocomplete-result-wrap"
                      v-on="resultListListeners"
                    >
                      <li
                        v-for="(result, index) in results"
                        :key="`autocomplete:${result.here_id || index}:${result.label}`"
                        :set-level="
                          level =
                            result.level !== undefined ? result.level - 1 : 0
                        "
                        :style="
                          result.match_level == 'noresults'
                            ? 'pointer-events: none'
                            : ''
                        "
                        :data-type="result.type"
                        v-bind="resultProps[index]"
                      >
                        <div
                          v-if="result.match_level == 'noresults'"
                          v-html="result.label"
                        ></div>
                        <div
                          v-else
                          class="booking-engine--autocomplete-result-item"
                          :style="
                            'padding-left:calc(' +
                              (result.level ? result.level - 1 : 0) +
                              '*0.5rem)'
                          "
                        >
                          <div class="booking-engine--autocomplete-result-icon">
                            <vb-icon
                              :name="get_type_icon(result.type)"
                              :color="sass('dark')"
                              :size="14"
                            />
                          </div>
                          <div
                            class="booking-engine--autocomplete-result-name"
                            v-html="result.name"
                          ></div>
                          <div
                            class="booking-engine--autocomplete-result-label"
                            v-html="result.label"
                          ></div>
                        </div>
                      </li>
                    </ul>
                  </b-col>
                </b-row>
              </template>
            </autocomplete>
          </div>
        </b-col>

        <!-- Dates (checkin-checkout) -->
        <b-col
          :md="!customizations.hide_location_search ? 3 : 5"
          @click="close_occupancy"
        >
          <div class="booking-engine--dates">
            <div
              class="booking-engine--dates--form cursor-pointer"
              @click.stop="datepicker_open = !datepicker_open"
            >
              <div class="booking-engine--dates--icon">
                <vb-icon
                  :name="'calendar'"
                  color="--primary"
                  :size="20"
                />
              </div>
              <b-form-input
                id="input-checkin"
                ref="input-checkin"
                :placeholder="dates_placeholder"
                class="vb-input"
              />
            </div>
            <div v-show="datepicker_open" class="vc-wrapper">
              <v-date-picker
                v-show="datepicker_open"
                :key="range.start + '-' + range.end + '-' + datepicker_open"
                v-model="range"
                is-range
                :min-date="min_date"
                :max-date="max_date"
                :disabled-dates="disabled_dates"
                :select-attribute="select_attrs"
                :drag-attribute="drag_attrs"
                @drag="(d) => on_drag_date(d)"
                @input="(d) => on_submit_date(d)"
              />
              <div
                v-if="get_config('guiSettings.minDisclaimer', '')"
                class="picker-alert"
              >
                <b-alert show variant="warning" size="xs">
                  {{ get_config('guiSettings.minDisclaimer', '') }}
                </b-alert>
              </div>
            </div>
          </div>
        </b-col>

        <!-- Occupancy -->
        <b-col
          v-click-outside="close_occupancy"
          :md="!customizations.hide_location_search ? 3 : 5"
          :xl="!customizations.hide_location_search ? 4 : 5"
          @click="close_datepicker"
        >
          <div class="booking-engine--occupancy">
            <div
              class="booking-engine--occupancy--form cursor-pointer"
              @click="occupancy_open = !occupancy_open"
            >
              <b-form-input
                id="input-occupancy"
                ref="input-occupancy"
                :placeholder="occupancy_placeholder"
                v-bind="occupancy"
                class="vb-input"
              />
              <div class="booking-engine--occupancy--icon">
                <vb-icon
                  :name="'occupants'"
                  color="--primary"
                  :size="20"
                />
              </div>
              <div class="booking-engine--occupancy--arrow">
                <vb-icon
                  :name="'angle_small_down'"
                  color="--primary"
                  :size="24"
                />
              </div>
            </div>
            <occupancy
              v-show="occupancy_open"
              v-bind="occupancy"
              :occupancy="occupancy"
              :max_children_per_room="
                get_config('guiSettings.occupancy.maxChildrenPerRoom', 4)
              "
              :max_adults_per_room="
                get_config('guiSettings.occupancy.maxAdultsPerRoom', 4)
              "
              :max_rooms="get_config('guiSettings.occupancy.maxRooms', 1)"
              :max_pax="get_config('guiSettings.occupancy.maxPax', 24)"
              @updated="
                (e) => {
                  this.$store.commit('occupancy', e.occupancy);
                }
              "
            >
              <template
                v-if="get_config('guiSettings.occupancy.maxRooms', 1) > 1"
                v-slot:title
              >
                <p
                  v-b-popover.hover.top.html="
                    'Il Beneficiario della prestazione (Viaggiatore principale) potrai essere tu, in qualità di titolare del credito welfare, o dovrai, in alternativa, indicare un familiare. Con &quot;familiare&quot; si intende una persona così come definita dal combinato disposto dell\'Art. 433 del Codice Civile e dell\'Art. 12 del D.P.R. del 22.12.1986 n. 917 e smi (TUIR).<br><br>Ti ricordiamo che ai sensi dell\'art. 76 del DPR 445/2000, le dichiarazioni mendaci, la falsità e l\'uso di fatti falsi sono punite ai sensi del Codice penale.'
                  "
                  class="semibold d-inline-flex align-items-center cursor-pointer"
                >
                  Quante camere?
                </p>
              </template>
              <template v-slot:disclaimer>
                <p class="text--sm mt-0 mb-0">
                  Età dei bambini il giorno del check-out
                </p>
              </template>
              <template v-slot:footer>
                <div class="occupancy--slot--footer">
                  <vb-button
                    :vb_options="{
                      text: 'Fatto',
                      variant: 'outline-info',
                      size: 'sm',
                    }"
                    @click.stop="close_occupancy"
                  />
                </div>
              </template>
            </occupancy>
          </div>
        </b-col>

        <!-- Search action -->
        <b-col md="2" :xl="!customizations.hide_location_search ? 1 : 2">
          <div class="booking-engine--search">
            <div class="vb-form text-center">
              <b-form-group
                label-for="input-autocomplete"
                label=""
                label-class="vb-name"
              >
                <vb-button
                  :vb_options="{
                    text: searching ? 'Attendi...' : 'Cerca',
                    disabled: searching || !search_enabled,
                    variant: 'info',
                  }"
                  @click="search"
                />
              </b-form-group>
            </div>
          </div>
        </b-col>
      </b-row>
    </section>
  </b-container>
</template>

<script>
  import { mapState } from 'vuex';
  import Occupancy from '@components/blocks/occupancy.vue';
  import InvadobaySuperIn from './invadobay/invadobay-super-in.vue';
  import { service_handler } from '@services';
  import {
    select_attrs,
    drag_attrs,
    stay_limit_disabled_dates,
  } from '@/utilities/calendar';
  import {
    encode_parameters,
    reverse_date,
    is_mobile,
    get_type_icon,
    rybbit_event,
  } from '@utilities';
  import moment from 'moment';
  import Fuse from 'fuse.js';

  // Distance (px) within which the booking engine is considered already
  // aligned with scrollIntoView's resting position, so a focus doesn't
  // re-trigger the scroll.
  const SCROLL_ALIGNED_TOLERANCE = 16;

  const MAX_STAY_NIGHTS = 28;

  export default {
    name: 'liveeps-booking-engine',

    components: {
      occupancy: Occupancy,
      'invadobay-super-in': InvadobaySuperIn,
    },

    props: {
      // Overrides constraints other than base logic
      date_constraints: {
        type: Object,
        default: () => ({}),
      },
      // Override base style customizations for specific scenarios
      customizations: {
        type: Object,
        default: () => ({}),
      },
      is_open: {
        type: Boolean,
        default: false,
      },
      // SuperIn context: { title, children } from the invadobay.details response.
      // Present only when the /invadobay/:slug landing is a parent (SuperIn).
      super_in: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      const { min_date, max_date } = this.get_date_limits();
      return {
        loading: false,
        autocomplete_focus: false,
        autocomplete_ready: true,
        autocomplete_searching: false,
        datepicker_open: false,
        datepicker_keep: false,
        occupancy_open: false,
        calendar_key: 0,
        calendar_config: {
          type: 'string',
          mask: 'YYYY-MM-DD', // Uses 'iso' if missing
        },
        min_date,
        max_date,
        drag_checkin: null,
        range: {
          /*
        start: new Date(2018, 0, 8),
        end: new Date(2018, 0, 12),
        */
        },
        suggestions: [],
        select_attrs,
        drag_attrs,
      };
    },

    computed: {
      // global state keys
      ...mapState(['occupancy', 'searching']),

      // module state keys
      ...mapState('liveeps', ['location', 'checkin', 'checkout']),

      search_params() {
        return {
          checkin: this.checkin,
          checkout: this.checkout,
          suggestion: this.location,
          occupancy: this.occupancy,
        };
      },

      autocomplete_placeholder() {
        const default_placeholder = 'Scegli una destinazione';
        if (this.autocomplete_focus) return default_placeholder;
        return this.location.label ? this.location.label : default_placeholder;
      },

      autocomplete_selected() {
        return !this.autocomplete_focus && !!this.location.label;
      },

      occupancy_placeholder() {
        let placeholder = '';
        let adults = 0;
        let children = 0;
        this.occupancy.forEach((room) => {
          adults += room.adults;
          children += room.children.length;
        });
        placeholder = `${adults} adult${adults === 1 ? 'o' : 'i'}`;
        if (children) {
          placeholder += `  •  ${children} bambin${children === 1 ? 'o' : 'i'}`;
        }
        if (this.occupancy.length > 1) {
          placeholder += `  •  ${this.occupancy.length} camere`;
        }
        return placeholder;
      },

      dates_placeholder() {
        if (this.checkin === '') return 'Scegli le date';
        const checkin = `${moment(this.checkin, 'YYYY-MM-DD').format(
          'ddd D'
        )} ${moment(this.checkin, 'YYYY-MM-DD').format('MMMM').slice(0, 3)}`;
        const checkout = `${moment(this.checkout, 'YYYY-MM-DD').format(
          'ddd D'
        )} ${moment(this.checkout, 'YYYY-MM-DD').format('MMMM').slice(0, 3)}`;
        return `${checkin} - ${checkout}`;
      },

      search_enabled() {
        if (this.location.label === '') return false;
        if (this.checkin === '') return false;
        if (this.checkout === '') return false;
        return true;
      },

      has_super_in() {
        return (
          Array.isArray(this.super_in.children) &&
          this.super_in.children.length > 0
        );
      },

      disabled_dates() {
        return stay_limit_disabled_dates(this.drag_checkin, MAX_STAY_NIGHTS);
      },
    },

    watch: {
      datepicker_open(open) {
        if (!open) this.drag_checkin = null;
      },
      'date_constraints.min_date'() {
        Object.assign(this, this.get_date_limits());
      },
      'date_constraints.max_date'() {
        Object.assign(this, this.get_date_limits());
      },
      checkin() {
        this.sync_range();
      },
      checkout() {
        this.sync_range();
      },
    },

    created() {
      this.encode_parameters = encode_parameters;
      this.reverse_date = reverse_date;
      this.is_mobile = is_mobile;
      this.get_type_icon = get_type_icon;
    },

    mounted() {
      /* eventuale gestione dell'evento 'suggestion' triggerato da altri componenti
    window.eventBus.$off('suggestion')
    window.eventBus.$on('suggestion', () => {
      this.search()
    })
    */
      this.sync_range();
    },

    methods: {
      get_date_limits() {
        let checkin_delay = this.get_config('guiSettings.minDays', 0);
        if (moment().hour() > 13) checkin_delay = checkin_delay + 1;

        let min_date = moment()
          .add(checkin_delay, 'days')
          .format('YYYY[-]MM[-]DD');
        if (
          this.date_constraints.min_date &&
          moment(this.date_constraints.min_date).diff(min_date) > 0
        ) {
          min_date = moment(this.date_constraints.min_date).format(
            'YYYY[-]MM[-]DD'
          );
        }

        let max_date = moment().add(1, 'years').format('YYYY[-]MM[-]DD'); // prenotazioni max 1 anno da oggi
        if (
          this.date_constraints.max_date &&
          moment(this.date_constraints.max_date).diff(max_date) < 0
        ) {
          max_date = moment(this.date_constraints.max_date).format(
            'YYYY[-]MM[-]DD'
          );
        }
        return { min_date, max_date };
      },
      sync_range() {
        if (!this.checkin || !this.checkout) return;
        const start = moment(this.checkin, 'YYYY-MM-DD');
        const end = moment(this.checkout, 'YYYY-MM-DD');
        if (
          this.range.start &&
          this.range.end &&
          start.isSame(this.range.start, 'day') &&
          end.isSame(moment(this.range.end, 'YYYY-MM-DD'), 'day')
        ) {
          return;
        }
        this.range = {
          start: start._d,
          end: end._d,
        };
      },
      scroll_into_view() {
        const booking_engine = this.$refs['booking-engine'];
        if (!booking_engine) {
          return;
        }
        const { top } = booking_engine.getBoundingClientRect();
        if (Math.abs(top) <= SCROLL_ALIGNED_TOLERANCE) {
          return;
        }
        booking_engine.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      close_occupancy() {
        this.occupancy_open = false;
      },
      close_datepicker() {
        if (this.datepicker_keep) {
          this.datepicker_keep = false;
          return;
        }
        this.datepicker_open = false;
      },
      hide_suggestions() {
        this.$refs.hidden_input.click();
      },
      // La libreria autocomplete scrolla l'elemento evidenziato solo tramite i
      // suoi ref interni (input/resultList), assenti quando si usa lo slot custom.
      // Replichiamo lo scroll sul contenitore ad ogni cambio di selectedIndex.
      on_autocomplete_update(results, selectedIndex) {
        if (selectedIndex < 0) return;
        this.$nextTick(() => {
          const wrap = this.$el.querySelector(
            '.booking-engine--autocomplete-result-wrap'
          );
          if (!wrap) return;
          const item = wrap.querySelector(
            `[data-result-index="${selectedIndex}"]`
          );
          if (!item) return;
          const wrap_rect = wrap.getBoundingClientRect();
          const item_rect = item.getBoundingClientRect();
          if (item_rect.top < wrap_rect.top) {
            wrap.scrollTop -= wrap_rect.top - item_rect.top;
          } else if (item_rect.bottom > wrap_rect.bottom) {
            wrap.scrollTop += item_rect.bottom - wrap_rect.bottom;
          }
        });
      },
      date_check(day) {
        return new Date(day.id) >= new Date();
      },
      autocomplete_blur() {
        this.$refs['autocomplete'].$data.value = '';
      },
      encode_search_params() {
        return encode_parameters(this.search_params);
      },
      async on_search_location(q) {
        // condizioni uscita chiamata autocomplete
        if (!q) return [];
        if (q === '') return [];
        if (q.length < 3) return [];

        this.autocomplete_searching = true;

        const config = this.get_config();
        const services = {
          here: 'here.autocomplete',
          geo: 'searchByGeo',
          property: 'searchByProperty',
          //in_salabam: 'searchInSalabamEndpoint',
        };
        const params = {
          apikey: config.hereApiKey,
          q,
          lang: 'it',
          limit: 5, // 5 default
          token: config.token, // local api
        };
        const search_services = [];
        const suggestions = [];
        let fused_suggestions = [];

        // here
        if (config.searchSettings.useHere) {
          search_services.push(
            service_handler(
              services.here,
              {
                params,
                service_name: services.here,
              },
              {
                modal: false,
                api_method: 'get',
                external: true,
              }
            )
          );
        }

        // geo
        if (config.searchSettings.useGeo) {
          search_services.push(
            service_handler(
              services.geo,
              {
                q,
                service_name: services.geo,
              },
              {
                modal: false,
              }
            )
          );
        }

        // property
        if (config.searchSettings.useProperty) {
          search_services.push(
            service_handler(
              services.property,
              {
                q,
                service_name: services.property,
              },
              {
                modal: false,
              }
            )
          );
        }

        // TODO
        // inSalabam
        //if(Vue.prototype.$config.searchSettings.useInSalabam){
        //  search_services.push(timedAxiosGet(this.searchInSalabamEndpoint, {params}, Vue.prototype.$config.searchSettings.axiosTimeout))
        //} // search use inSalabam

        try {
          const settled = await Promise.allSettled(search_services);
          const results = settled
            .filter((r) => r.status === 'fulfilled')
            .map((r) => r.value);
          results.forEach((result) => {
            let search_service = '';
            if (result.config.service_name === services.here) {
              search_service = services.here;
            } else if (
              JSON.parse(result.config.data).service_name === services.geo
            ) {
              search_service = services.geo;
            } else if (
              JSON.parse(result.config.data).service_name === services.property
            ) {
              search_service = services.property;
            }
            let temp = [];
            switch (search_service) {
              case services.geo:
              case services.property:
                //case services.in_salabam:
                temp = result.data.suggestions || result.data.data;
                temp.forEach((suggestion) => {
                  suggestion.type = suggestion.match_level;
                  suggestion.sorting = 0;
                  if (
                    suggestion.countryCode === 'CUB' ||
                    suggestion.countryCode === 'CU'
                  ) {
                    return false;
                  } // escludo cuba
                  suggestions.push(suggestion);
                });
                break;

              case services.here:
                result.data.items.forEach((suggestion, index) => {
                  if (suggestion.address.countryCode === 'CUB') return; // escludo cuba
                  const i = index + 1;
                  let temp_label = '';
                  let suggestion_sorting = 0;

                  switch (suggestion.resultType) {
                    case 'houseNumber':
                      suggestion.match_level = suggestion.houseNumberType; // PA, interpolated
                      break;
                    case 'locality':
                      suggestion.match_level = suggestion.localityType; // postalCode, district, subdistrict, city
                      switch (suggestion.localityType) {
                        case 'city':
                          suggestion_sorting = 100;
                          temp_label = `Città di ${suggestion.address.city}`;
                          if (suggestion.address.state) {
                            temp_label += `, ${suggestion.address.state}`;
                          }
                          break;
                        case 'district':
                        case 'subdistrict':
                          suggestion_sorting = 50;
                          temp_label = `Quartiere ${suggestion.address.district}, ${suggestion.address.city}`;
                          break;
                        case 'postalCode':
                          suggestion.name = suggestion.title;
                          temp_label = suggestion.title;
                          break;
                        default:
                          break;
                      }
                      break;
                    case 'administrativeArea':
                      suggestion.match_level =
                        suggestion.administrativeAreaType; // county, state, country
                      switch (suggestion.administrativeAreaType) {
                        case 'county':
                          suggestion_sorting = 90;
                          temp_label = `Provincia di ${suggestion.address.county}`;
                          if (suggestion.address.state) {
                            temp_label += `, ${suggestion.address.state}`;
                          }
                          break;
                        case 'state':
                          suggestion_sorting = 10;
                          temp_label = `Regione ${suggestion.address.state}`;
                          break;
                        case 'country':
                          temp_label = suggestion.address.country;
                          break;
                        default:
                          break;
                      }
                      break;
                    case 'addressBlock':
                      suggestion.match_level = suggestion.addressBlockType; // block, subblock
                      break;

                    //case "postalCodePoint":
                    //case "street":
                    //case "intersection":
                    //case "place":
                    default:
                      suggestion.match_level = suggestion.resultType;
                      suggestion.name = suggestion.title;
                      temp_label = suggestion.title;
                      break;
                  }

                  suggestion.name = suggestion.address[suggestion.match_level];
                  suggestion.type = suggestion.match_level;

                  if (suggestion.address.countryCode === 'ITA') {
                    suggestion_sorting += 100; // prima ITA (principale mercato)
                  } else {
                    //temp_label += ' (' + suggestion.address.county + ', ' + suggestion.address.countryName + ')'
                    const countyStr = suggestion.address.county
                      ? `${suggestion.address.county},`
                      : '';
                    const countryStr = suggestion.address.countryName
                      ? ` ${suggestion.address.countryName}`
                      : '';
                    temp_label += `(${countyStr}${countryStr})`;
                  }

                  // se si cambia la posizione di label
                  // o la struttura di suggestion
                  // cambiare il serializer dell'autocomplete
                  suggestion.label = temp_label;

                  // sottraendo i faccio si che a parità di suggestion_sorting
                  // vengano comunque mostrati i suggerimenti secondo l'ordine ritornato dall'api
                  suggestion.sorting = suggestion_sorting - i;

                  suggestion.here_id = suggestion.id;
                  delete suggestion.id;

                  suggestions.push(suggestion);
                });
                break;

              default:
                break;
            }
          });

          const options = {
            // isCaseSensitive: false,
            // includeScore: false,
            // shouldSort: true,
            // includeMatches: false,
            // findAllMatches: false,
            minMatchCharLength: 3,
            // location: 0,
            threshold: 0.3, //[0,...,1] default 0.6 (0 => perfect match, 1 => match anything)
            // distance: 100,
            // useExtendedSearch: false,
            // ignoreLocation: false,
            // ignoreFieldNorm: false,
            // fieldNormWeight: 1,
            keys: [
              {
                name: 'fusehint',
                weight: 7,
              },
              {
                name: 'address.city',
                weight: 3,
              },
              {
                name: 'name',
                weight: 2,
              },
              'label',
            ],
          };

          const fuse = new Fuse(suggestions, options);

          fused_suggestions = fuse.search(q).map((s) => s.item);
        } catch (error) {
          this.log('[on_search_location] - error');
          this.log(error);
        } finally {
          this.autocomplete_searching = false;
        }

        if (fused_suggestions.length === 0) {
          fused_suggestions.push({
            type: 'msg',
            name: 'nessun risultato',
            label: 'nessun risultato',
            match_level: 'noresults',
          });
        }
        return fused_suggestions;
      },
      search() {
        // site specific logic
        // ...

        // shared logic
        this.$store.commit(
          'liveeps/current_search',
          this.encode_search_params()
        );
        this.$store.commit('liveeps/last_search', this.encode_search_params());
        this.$store.commit('searching', true);
        this.$store.commit('liveeps/trigger_search', true);
        this.$emit('search', {
          reset_page_filters: true,
          reset_advanced_filters: true,
          reset_search_results: true,
        });
        this.track_form({
          event: 'booking-engine:search:submit',
          data: { type: 'info' },
        });
      },
      on_drag_date(dates) {
        if (!dates || !dates.start || !dates.end) {
          this.drag_checkin = null;
          return;
        }
        const start = moment(dates.start);
        if (start.isSame(moment(dates.end), 'day')) {
          this.drag_checkin = start.format('YYYY-MM-DD');
        }
      },
      on_submit_date(dates) {
        this.drag_checkin = null;
        this.datepicker_keep = true;
        const checkin = moment(dates.start).format('YYYY[-]MM[-]D');
        let checkout = moment(dates.end).format('YYYY[-]MM[-]D');
        if (checkin === checkout) {
          checkout = moment(dates.end).add(1, 'days').format('YYYY[-]MM[-]D');
          this.range.end = moment(dates.end)
            .add(1, 'days')
            .format('YYYY[-]MM[-]D');
        }
        this.close_datepicker();
        this.$store.commit('liveeps/checkin', checkin);
        this.$store.commit('liveeps/checkout', checkout);
      },
      on_submit_location(suggestion) {
        // guard clause: exit if suggestion is undefined or null
        if (!suggestion) return;

        this.autocomplete_searching = true;
        this.hide_suggestions();
        this.$store.commit(
          'liveeps/current_search',
          this.encode_search_params()
        );

        // serve ad evitare che al click sulla suggestion già attiva compaia [Object Object] nell'input testuale
        this.$nextTick(() => {
          this.$refs['autocomplete'].$data.value = '';
          this.autocomplete_searching = false;
          // fix per mancato aggiornamento placeholder su ipad
          this.$nextTick(() => {
            this.autocomplete_focus = false;
          });
        });

        // es. suggestion "nessun risultato"
        if (suggestion.type && suggestion.type === 'msg') return;

        // Rybbit: traccia selezione suggestion
        rybbit_event('suggestion:select', {
          label: suggestion.label || '',
          type: suggestion.type || '',
          match_level: suggestion.match_level || '',
        });

        const location = {
          label: suggestion.label,
          match_level: suggestion.match_level,
          name: suggestion.name || '',
          latitude: '',
          longitude: '',
        };

        if (suggestion.property_id) {
          location.property_id = suggestion.property_id;
        }

        // se ho già le coordinate NON serve la chiamata al geocode
        // è il caso di searchByProperty (in cui ho id che può essere sia positivo sia negativo), e searchByGeo (in cui l'id non c'è per niente)
        // la chiamata a lookup va fatta solo nel caso di suggestion here, che ha un id del tipo "here:cm:namedplace:20116469"
        if (!suggestion.here_id) {
          this.$store.commit('liveeps/location', {
            ...location,
            match_level: suggestion.match_level,
            latitude: suggestion.latitude,
            longitude: suggestion.longitude,
          });
          return;
        }

        // se NON ho ancora le coordinate devo interrogare
        const config = this.get_config();
        const params = {
          apikey: config.hereApiKey,
          language: 'it',
          id: suggestion.here_id,
        };

        service_handler(
          'here.lookup',
          {
            params,
            service_name: 'here.lookup',
          },
          {
            modal: false,
            api_method: 'get',
            external: true,
          }
        ).then(
          (success) => {
            this.log('[here.lookup] - success');
            this.$store.commit('liveeps/location', {
              ...location,
              latitude: success.data.position.lat,
              longitude: success.data.position.lng,
            });
          },
          (error) => {
            this.log('[here.lookup] - error');
            this.log(error.response);
            window.eventBus.$emit('got_toast_notification', {
              text: 'Ops! Abbiamo problemi tecnici con questa destinazione e ci stiamo lavorando. Fai un nuovo accesso e cerca una nuova destinazione.',
              //timer: 999999, // default 10000
              variant: 'danger',
            });
          }
        );
      },
    },
  };
</script>

<style lang="scss">
  /* rimosso lo scoped: non permetteva di modificare lo style delle tabs */
  .booking-engine {
    $gutter: 20px;
    @include make-grid-columns(
      $columns: $grid-columns,
      $gutter: $gutter,
      $breakpoints: $grid-breakpoints
    );
    .row {
      margin-left: -$gutter/2;
      margin-right: -$gutter/2;
    }
    position: relative;
    z-index: 100;
    max-width: 1175px;
    margin: 0 auto;
    background: $extra2;
    padding: $grid-gutter-width/2;
    border-radius: $border-radius-lg * 2;
    border: 1px solid $white;
    box-shadow:
      0 3px 12px 0 rgba(0, 0, 0, 0.1),
      0 1px 2px 0 rgba(0, 0, 0, 0.08);

    @include media-breakpoint-down(md) {
      display: none;

      @at-root .homepage-view & {
        display: block;
      }
    }

    &--is-open {
      display: block;

      @include media-breakpoint-down(md) {
        margin-top: $grid-gutter-20;
        position: absolute;
        left: $grid-gutter-width;
        right: $grid-gutter-width;
        box-shadow: 0 0 15px 0 rgba($dark, 0.2);
      }
    }

    &--glass {
      background-color: rgba(white, 0.75);
    }
    &--inline {
      margin: 0;
    }
    // The SuperIn title tab sits on the top-left corner
    &--super-in {
      border-top-left-radius: 0;
    }
    &--items-2 {
      max-width: 800px;
    }

    & > .row > * {
      position: relative;
      &:nth-child(1) {
        z-index: 4;
      }
      &:nth-child(2) {
        z-index: 3;
      }
      &:nth-child(3) {
        z-index: 2;
      }
      &:nth-child(4) {
        z-index: 1;
      }
      &:not(:last-child) {
        margin-bottom: $gutter/2;
      }
      @include media-breakpoint-up(md) {
        &:nth-child(1n) {
          margin-bottom: 0;
        }
      }
    }

    &--panel {
      background-color: $white;
      position: absolute;
      width: 100%;
      top: 100%;
      margin: $gutter/4 0 0 0;
      padding: $gutter/2 $gutter;
      border: 1px solid $gray-200;
      box-shadow: $box-shadow-sm;
      border-radius: $btn-border-radius;
    }

    @include media-breakpoint-up(md) {
      & > .row {
        & > *:nth-child(2),
        & > *:nth-child(3),
        & > *:nth-child(4) {
          padding-left: 0;
        }
      }
    }

    // Focus Button
    .focus-dump-button {
      opacity: 0;
      position: absolute;
      left: -999999px;
      top: -999999px;
    }

    // Input
    .form-group {
      margin-bottom: 0;
    }

    input {
      &::placeholder {
        font-size: $font-size-base;
        color: $vb-body-color;
      }
    }

    &--autocomplete--form input {
      &::placeholder {
        color: var(--primary);
      }

      &:placeholder-shown {
        font-style: italic;
      }

      &.autocomplete-input-selected:placeholder-shown {
        font-style: normal;
        font-weight: 600;
      }
    }

    &--autocomplete--loading {
      position: absolute;
      top: 50%;
      right: $grid-gutter-width/4;
      transform: translateY(-50%);
      background-color: $white;
    }

    #input-occupancy,
    #input-checkin {
      pointer-events: none;
    }

    // icons
    &--occupancy,
    &--dates,
    &--autocomplete {
      &--form {
        position: relative;

        input {
          padding-left: $gutter * 2;
          border: 1px solid var(--primary);
          font-size: $font-size-base;
        }
      }
      &--icon {
        position: absolute;
        top: 50%;
        left: $gutter/2;
        height: 26px;
        transform: translateY(-50%);
        aspect-ratio: 1 / 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      &--arrow {
        position: absolute;
        top: 50%;
        right: $gutter/2;
        height: 24px;
        transform: translateY(-50%);
        aspect-ratio: 1 / 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &--search {
      .vb-btn {
        width: 100%;
      }
    }

    &--autocomplete {
      // classi generate con vue-autocomplete
      &-result {
        padding: $gutter/4 $gutter;
        position: relative;

        &[aria-selected='true'] {
          position: relative;
          z-index: 1;
          box-shadow: inset 0 0 0 2px var(--primary);
          border-radius: $btn-border-radius;

          // nasconde la linea separatrice, altrimenti si sovrappone al bordo
          &:not(:last-child):after {
            display: none;
          }
        }

        &:not(:last-child) {
          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: $gutter;
            right: $gutter;
            height: 1px;
            background: $gray-200;
          }
        }

        &-item {
          padding: $gutter/4 0;
          cursor: pointer;
          text-transform: capitalize;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          * {
            color: inherit;
            transition: $transition-base;
          }
          &:hover * {
            color: var(--info);
            --color: var(--info);
          }
        }

        &-icon {
          margin-right: $gutter/4;
          width: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        &-name {
          font-weight: $vb-font-weight-semibold;
          color: $gray-900;
        }

        &-label {
          min-width: 100%;
          font-size: $font-size-sm;
        }

        &:hover {
          color: var(--info);
        }

        &:last-child {
          border-bottom: 0;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        // wrap li, contenitore per la scrollbar
        &-wrap {
          @extend .booking-engine--panel;
          overflow: auto;
          max-height: 250px;

          // solo per firefox
          scrollbar-width: thin;
          scrollbar-color: $gray-500 transparent;
          //

          &::-webkit-scrollbar {
            width: 4px;
          }

          &::-webkit-scrollbar-track {
            box-shadow: none;
          }

          &::-webkit-scrollbar-thumb {
            background-color: $gray-600;
            border-radius: 2.5px;
          }
        }

        // ul
        &-list {
          max-width: calc(100% - #{$gutter});
          padding: 0;
          list-style: none;
          li {
            list-style: none;
            &:last-child {
              margin-bottom: $gutter/2;
            }
          }
        }
      }
    }

    &--dates {
      position: relative;
      .vc-container {
        @extend .booking-engine--panel;
        padding-left: $gutter/2;
        padding-right: $gutter/2;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
      .vc-weeks {
        padding: 0;
        margin-top: $gutter/2;
      }
      .vc-arrows-container,
      .vc-header {
        padding: 0;
        margin: 0;
      }

      .vc-container {
        --text-xs: 12px;
        --text-sm: 13px;
        --text-base: 14px;
        --font-family: #{$font-family-base};
      }

      .is-not-in-month {
        min-height: 0 !important;
        max-height: 0 !important;
        overflow: hidden;
      }

      .picker-alert {
        margin-top: -5px;
        .alert {
          font-size: $font-size-md;
          line-height: 1.25;
          text-align: center;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }
      }

      @include media-breakpoint-between(md, lg) {
        .vc-container,
        .picker-alert {
          min-width: 280px;
        }
      }
    }

    &--occupancy {
      position: relative;
      .occupancy {
        @extend .booking-engine--panel;
        @include media-breakpoint-down(smd) {
          padding-left: $grid-gutter-width/2;
          padding-right: $grid-gutter-width/2;
        }
      }
    }

    .occupancy--slot--footer {
      margin-top: $gutter;
      .vb-btn {
        display: block;
        width: 100%;
      }
    }

    .popover {
      * {
        border: 0px;
        box-shadow: none;
      }
    }

    @include media-breakpoint-down(sm) {
      &--vc-wrapper {
        position: static !important;
        max-width: 100%;
        margin-top: 0;
        li {
          padding: $grid-gutter-width/6 $grid-gutter-width/2;
          .booking-engine--autocomplete-result-label {
            padding-left: 24px;
            line-height: 100%;
          }
        }
      }
      &--dates {
        .vc-container {
          position: static;
          margin-top: 0;
        }
      }
      &--occupancy .occupancy {
        position: static;
        margin-top: 0;
      }
    }

    .vc-wrapper {
      @include media-breakpoint-up(md) {
        position: absolute;
        top: 53px;
        left: 0;
        width: 100%;
      }
    }
    &--autocomplete-result-list {
      position: static !important;
      max-width: 100%;
      margin-top: 0;
      li {
        padding: $grid-gutter-width/6 $grid-gutter-width/2;
        .booking-engine--autocomplete-result-label {
          padding-left: 24px;
          line-height: 100%;
        }
      }

      @include media-breakpoint-up(md) {
        position: absolute !important;
      }
    }
    &--dates {
      .vc-container {
        position: static;
        margin-top: 0;
      }
    }
    &--occupancy .occupancy {
      left: 0;
      margin-top: 0;
    }
  }
</style>
